import * as moment from "moment-timezone";
import Swal from "sweetalert2";
import { Component, Injectable, Input, OnDestroy, OnInit } from "@angular/core";
import { NgbDatepickerI18n } from "@ng-bootstrap/ng-bootstrap";
import { GasprecioService } from "../../../services/gasprecio.service";
import {
  FuelOrdersService,
  isMobicoPipesAdmin,
} from "../../../services/fuel-orders.service";
import { AuthService } from "src/app/services/auth.service";
import { SelectionType } from "@swimlane/ngx-datatable";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { SharedService } from "../../../services/shared.service";
import { CustomAlertService } from "src/app/core/services/custom-alert.service";
import { Subscription, concat, firstValueFrom } from "rxjs";

/** Una fila del formulario de solicitud = una pipa (pedido de fuel-orders). */
interface OrderFormRow {
  station: string; // en modo grupo la elige el usuario; en estación es implícita
  loadDate: string;
  capacity: number | null;
  po: string;
  material: string;
  product: string;
  // Candado de inventario: espacio disponible proyectado a la fecha requerida
  // (GET /fuel-orders/suggest?date=). null = sin datos, no se bloquea.
  availableSpace?: number | null;
  spaceLoading?: boolean;
  // true cuando la consulta ya respondió: permite avisar "sin datos" (null) en la UI.
  spaceChecked?: boolean;
  // 'real' = lectura del volumétrico en la fecha de entrega · 'theoretical' = sin
  // lectura, inventario teórico del Cronograma (fix back 2026-07-13).
  spaceBasis?: string | null;
  // Desglose del espacio (campos nuevos de /suggest): alimenta el modal
  // "¿Por qué no hay espacio?" — capacidad − inventario a la fecha − arrastre.
  tankCapacity?: number | null;
  inventoryUsed?: number | null;
  inventoryDate?: string | null;
  carryOver?: number | null;
}

/** Español para el mini calendario del cambio de fecha (scoped a este componente). */
@Injectable()
export class PipesDatepickerI18n extends NgbDatepickerI18n {
  private readonly weekdays = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
  private readonly months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  getWeekdayShortName(weekday: number): string {
    return this.weekdays[weekday - 1];
  }
  getMonthShortName(month: number): string {
    return this.months[month - 1].slice(0, 3);
  }
  getMonthFullName(month: number): string {
    return this.months[month - 1];
  }
  getDayAriaLabel(date: { year: number; month: number; day: number }): string {
    return `${date.day}/${date.month}/${date.year}`;
  }
}

@Component({
  selector: "app-pipes",
  templateUrl: "./pipes.component.html",
  styleUrls: ["./pipes.component.scss"],
  providers: [{ provide: NgbDatepickerI18n, useClass: PipesDatepickerI18n }],
})
export class PipesComponent implements OnInit, OnDestroy {
  // Modo "solicitudes de la estación": muestra fuel_orders de UNA estación
  // (no compras del grupo). Lo activa el tab Programa de Solicitud de Pipas.
  @Input() source = ""; // '' = compras (Tablero) | 'orders' = solicitudes
  @Input() onlyStation = false; // true = exclusivo de la estación logueada
  // true = flujo completo de "Solicitud de Pipas" (botón hero + tabla Solicitudes + Cronograma
  // con altura fija). false = solo el cronograma tal cual (vista "Cronograma" de Tablero).
  @Input() requestFlow = false;
  // true = SOLO la card del Cronograma del flujo (página "Cronograma" del menú de
  // estación, task 2026-07-10 "mover el cronograma a su propia opción"): oculta
  // "Nueva solicitud" y "Solicitudes", y silencia los toasts de respuestas (esos
  // avisos viven en la vista de solicitud; consumirlos acá los perdería, el
  // de-dupe es por localStorage). Se usa junto con source='orders' + onlyStation
  // + requestFlow para conservar el mismo fetch/estado de la guía.
  @Input() cronoOnly = false;

  // ----- Modal de solicitud de pipa (fuel-orders, varias pipas por solicitud) -----
  // En modo estación la estación es implícita (idStation); en modo grupo se elige por fila.
  showOrderForm = false;
  savingOrder = false;
  orderEditMode = false; // true = editar una pipa existente (una sola fila)
  editingOrderId = ""; // _id de la pipa en edición
  orderForms: OrderFormRow[] = [this.emptyOrderForm()];
  // Nombres comerciales MOBICO (Extra/Supreme/Diésel) → interno GASPRE (regular/premium/diesel).
  readonly orderProductOptions: { value: string; label: string }[] = [
    { value: "regular", label: "Extra" },
    { value: "premium", label: "Supreme" },
    { value: "diesel", label: "Diésel" },
  ];

  // REGLAS DE TIEMPO MOBICO — DESACTIVADAS por decisión del equipo (2026-07-06):
  // pedidos y cambios se reciben en cualquier momento. Se conservan comentadas por
  // si se vuelven a requerir (buscar "REGLA DESACTIVADA" aquí y en el backend).
  // Ventana de pedido semanal: solo lunes 09:00-14:00 hrs (MX).
  // readonly orderWindowMsg = 'Los pedidos se reciben únicamente los lunes de 09:00 a 14:00 hrs. Fuera de ese horario solo se aceptan solicitudes de cambio sobre pedidos ya registrados.';
  //
  // get isOrderWindowOpen(): boolean {
  //   const now = moment.tz('America/Mexico_City');
  //   return now.isoWeekday() === 1 && now.hour() >= 9 && now.hour() < 14;
  // }

  /**
   * Mensaje de error del API para mostrar en SweetAlert. El ErrorNormalizerInterceptor
   * entrega {status, message, raw}: el body original ({err: '...'}) queda en raw, y
   * message trae el objeto serializado (ilegible). Se cubre también la forma cruda
   * (err.error.err) por si la petición no pasó por el normalizador.
   */
  private apiErrorMsg(err: any, fallback: string): string {
    const msg = err?.raw?.error?.err || err?.error?.err;
    return typeof msg === "string" && msg ? msg : fallback;
  }

  // La carga inicial se difiere a ngOnInit: los @Input se asignan DESPUÉS del
  // constructor, y los BehaviorSubject de SharedService emiten dentro de él.
  private inited = false;

  date: any;
  endDate;
  first = true;
  id;
  idGroup = "60afa99655f4ee369d1df409";
  idGroupSub$!: Subscription;
  idPurchase;
  idStation;
  idZone = "";
  loading = false;
  marketerName = "";
  marketSelected = "";
  max = moment().format("YYYY-MM-DD");
  maxDate = moment().add(30, "days").format("YYYY-MM-DD");
  mode = "add";
  newForm: UntypedFormGroup;
  product: any = "";
  reqst = 0;
  rol;
  rows: any;
  selected: any[] = [];
  SelectionType = SelectionType;
  shiftSelected = "";
  showBurgos = false;
  showCombuexpress = false;
  showEnerser = false;
  showGroups = false;
  showRuta = false;
  showMarketers = false;
  showRendichicas = false;
  showStations = false;
  showStatus = false;
  showWeeks = false;
  showWorkShifts = false;
  showMobico = false;
  showMegagas = false;
  startDate;
  stationSelected = "";
  statusName = "";
  statusSelected = "";
  temp: any = [];
  val: any;
  // ----- Altura fija de las tablas (Solicitudes y Cronograma): N filas visibles,
  // con relleno "—" cuando hay menos. SIN paginación (pedido 2026-07-13 "tipo
  // scroll infinito", espejo del panel admin): todo el conjunto vive bajo el
  // scroll interno de cada tabla y se PINTA por lotes conforme el scroll se
  // acerca al fondo (los datos ya están completos en memoria; el corte es solo
  // de render — estas tablas van con scrollbarV=false a propósito). -----
  // 10 filas (task 2026-07-10 "más elementos por página"): con 5 la vista de
  // solicitud dejaba media pantalla vacía bajo la tabla. Acoplado en SCSS:
  // .orders-summary-scroll (max-height 370px = 50 header + 10×32) y el
  // min-height 320px de su .empty-row (empty state = tabla llena).
  readonly pipesPageSize = 10;
  private readonly renderBatch = 30;
  private ordersRenderLimit = 30;
  private cronoRenderLimit = 30;
  // Página más larga para el Cronograma del Tablero (view=pipes, task 2026-07-10
  // "ajustar el espacio vacío"): ahí la tabla es el ÚNICO contenido de la página,
  // así que 5 filas dejaban un hueco enorme bajo la card. 15 filas llenan mejor
  // el viewport sin scroll interno (la página escrolea si hace falta). En el
  // flujo (requestFlow) se conservan 5: la card comparte pantalla con Solicitudes.
  readonly cronoBoardPageSize = 15;
  get cronoPageSize(): number {
    // Página larga siempre que el Cronograma sea el único contenido: Tablero
    // (!requestFlow) y página "Cronograma" de estación (cronoOnly).
    return this.requestFlow && !this.cronoOnly
      ? this.pipesPageSize
      : this.cronoBoardPageSize;
  }
  /**
   * Altura del empty state del Cronograma (task 2026-07-10 "empty states"): la
   * card vacía debe medir LO MISMO que con datos. Con datos el cuerpo mide
   * cronoPageSize × 32px (getRowHeight; el relleno de applyCronoPage empareja
   * las páginas cortas), así que el .empty-row reserva esa misma altura vía la
   * CSS var --crono-empty-height (se fija en el host de la ngx-datatable y el
   * SCSS la lee con ::ng-deep). Dinámica porque el pageSize varía por vista
   * (5 flujo / 15 Tablero y página Cronograma).
   */
  get cronoEmptyHeight(): string {
    return `${this.cronoPageSize * 32}px`;
  }
  // Total de pedidos padre del conjunto filtrado (guard del scroll incremental).
  private ordersTotalItems = 0;
  // Conjunto filtrado COMPLETO del cronograma (antes de recortar por página);
  // this.temp guarda solo la página visible que consume la ngx-datatable.
  cronogramaFiltered: any[] = [];
  // Filtro por producto de la tabla (aplica en estación y Tablero). '' = todos.
  productFilter = "";
  // Filtro por producto PROPIO del Cronograma (banda de filtros de esa card en el
  // flujo de solicitudes; task 2026-07-09 "agregar filtros en cronograma"). Estado
  // aparte de productFilter a propósito: cada card filtra lo suyo — los filtros de
  // Solicitudes ya NO tocan el cronograma en requestFlow (antes sí, y un estatus
  // distinto de Confirmada lo vaciaba en silencio porque cronogramaBase solo trae
  // confirmados). '' = todos. Reusa las opciones de orderFilterProducts.
  cronoProductFilter = "";
  readonly orderFilterProducts: { value: string; label: string }[] = [
    { value: "", label: "Todos los productos" },
    { value: "regular", label: "Regular" },
    { value: "premium", label: "Premium" },
    { value: "diesel", label: "Diésel" },
  ];
  // Filtro por estatus (fuel-orders). El ciclo completo del flujo con admin MOBICO:
  // pending (al enviar) → confirmed / rejected / review (respuesta del admin);
  // cancelled = cancelada por el solicitante (solo si aún no está aprobada). '' = todos.
  statusFilter = "";
  readonly orderFilterStatuses: { value: string; label: string }[] = [
    { value: "", label: "Todos los estatus" },
    { value: "pending", label: "Pendiente" },
    { value: "confirmed", label: "Confirmada" },
    { value: "rejected", label: "Rechazada" },
    { value: "review", label: "En revisión" },
    { value: "cancelled", label: "Cancelada" },
  ];
  // Filtro por estación en la barra moderna de pipes-request (Tablero, requestFlow && !onlyStation).
  // Guarda el station_id (this.stations = {label: alias, value: _id}, ver getStationsGroup): las
  // filas de this.rows (y por lo tanto cronogramaFiltered/requestRows, que las spread-ean tal cual)
  // ya traen station_id de origen (lo usa también openEditOrder), así que matcheamos por ese id en
  // vez del alias/station_name para evitar falsos positivos si dos estaciones compartieran alias.
  // '' = todas.
  stationFilter = "";
  // Solicitudes de cambio del SOLICITANTE sobre sus pedidos (proceso MOBICO: el
  // cliente solicita, MOBICO aprueba o rechaza). Junto con los pedidos alimentan
  // la tabla única "Solicitudes" (pendientes y resueltas, sin secciones aparte).
  changesRows: any[] = [];
  readonly changeTypeLabels: { [key: string]: string } = {
    addition: "Adición",
    cancel: "Cancelación",
    date: "Fecha",
    capacity: "Capacidad",
    product: "Producto",
  };
  // La columna "Respuesta MOBICO" muestra el estatus (ya no hay texto libre).
  readonly changeStatusLabels: { [key: string]: string } = {
    pending: "Pendiente",
    review: "En revisión",
    confirmed: "Confirmada",
    rejected: "Rechazada",
    cancelled: "Cancelada",
  };
  // Solicitudes de cambio SIN resolver por pedido: bloquean pedir otro cambio o
  // cancelación sobre el mismo pedido (un solo cambio en vuelo, igual que el backend).
  pendingChangesByOrder = new Map<string, any[]>();
  // Tabla única "Solicitudes": una fila por pedido. Al expandir un pedido, sus filas
  // de historial (pedido de origen + cada cambio) se insertan debajo ocupando las
  // mismas columnas. El Tipo y el estatus de la fila padre reflejan la solicitud en
  // curso: el cambio en vuelo si lo hay, si no el pedido.
  requestRows: any[] = [];
  // Pedidos con su historial expandido (por _id): controla el dropdown de filas hijas.
  expandedHistory = new Set<string>();
  // Historiales en cierre animado: las filas siguen en la tabla con `_closing`
  // hasta que termina la animación de salida y se retiran del array.
  closingHistory = new Set<string>();
  // Identidad de fila para el bookkeeping interno de ngx-datatable (selección,
  // índices). OJO: el trackBy real del *ngFor es [trackByProp]="'_id'" en el
  // template — rowTrackingFn ignora rowIdentity y sin trackByProp trackea por
  // índice posicional (lo que re-animaba filas al insertar/quitar historial).
  rowIdentity = (row: any) => String(row?._id ?? "");
  // Empty states de las tablas del flujo: misma línea visual que el recuadro
  // "Solicita una pipa" (ícono tenue + título + texto guía centrados).
  // ngx-datatable renderiza emptyMessage con innerHTML.
  readonly emptyRequestsMessage = `
    <div class="pipes-empty">
      <i class="fa fa-inbox" aria-hidden="true"></i>
      <span class="pipes-empty__title">Aún no tienes solicitudes esta semana</span>
      <span class="pipes-empty__text">Crea un pedido con el botón "Nuevo pedido" y dale seguimiento aquí.</span>
    </div>`;
  readonly emptyCronogramaMessage = `
    <div class="pipes-empty">
      <i class="fa fa-truck" aria-hidden="true"></i>
      <span class="pipes-empty__title">Sin entregas programadas</span>
      <span class="pipes-empty__text">Aquí aparecen los pedidos aprobados por MOBICO.</span>
    </div>`;
  // Aviso único de solicitudes resueltas por MOBICO (ids ya avisados en localStorage);
  // se notifica con un toast resumen (no bloqueante, se cierra solo), el detalle
  // vive en la tabla Solicitudes.
  private readonly noticeStorageKey = "pipesResolvedNotified";
  private ordersLoadedForNotice = false;
  private changesLoadedForNotice = false;
  // Confirmación de cancelación: modal op-confirm de la familia del flujo (header
  // rojo, tarjeta del pedido, error inline y botón con avance).
  cancelTarget: any = null;
  cancellingOrder = false;
  cancelError = "";
  // ----- Edición DIRECTA de la entrega desde el cronograma (solo admin MOBICO):
  // fecha de entrega real y costos, sin flujo de aprobación -----
  isMobicoAdmin = isMobicoPipesAdmin();
  deliveryEditRow: any = null;
  deliveryEditDate = "";
  deliveryEditTotal: number | null = null;
  deliveryEditPrice: number | null = null;
  savingDeliveryEdit = false;
  deliveryEditError = "";
  // ----- Modal "Realizar cambio" (solicitud de cambio sobre un pedido) -----
  showChangeForm = false;
  savingChange = false;
  changeRow: any = null;
  changeForm = this.emptyChangeForm();
  // Candado de inventario del cambio: espacio disponible con los valores efectivos.
  changeSpace: number | null = null;
  changeSpaceLoading = false;
  changeSpaceChecked = false;
  changeSpaceBasis: string | null = null;
  // Inventario actual del tanque (stock_today del suggest): se muestra junto al espacio.
  // Desglose del espacio del cambio (campos nuevos de /suggest, fix back
  // 2026-07-13): alimenta el modal "¿Por qué no hay espacio?".
  changeTankCapacity: number | null = null;
  changeInventoryUsed: number | null = null;
  changeInventoryDate: string | null = null;
  changeCarryOver: number | null = null;
  readonly changeTypeOptions: { value: string; label: string }[] = [
    { value: "date", label: "Cambio de fecha" },
    { value: "capacity", label: "Cambio de capacidad" },
    { value: "product", label: "Cambio de producto" },
  ];
  weeks: any = [];
  weekSelected: any;
  weekSelected$!: Subscription;
  zone$!: Subscription;
  products = [
    { label: "Regular", value: "regular" },
    { label: "Premium", value: "premium" },
    { label: "Diesel", value: "diesel" },
  ];

  marketers = [];
  stations = [];
  // Opciones del ngx-select de Estación del filtro (barra "Solicitudes", requestFlow &&
  // !onlyStation): copia de this.stations con una opción "Todas las estaciones" (value '')
  // al principio. Lista APARTE (no se mezcla con this.stations, que comparten el modal y
  // el formulario de "Nueva solicitud") porque acá con allowClear=false necesitamos una
  // opción explícita para volver a "sin filtro" (antes lo daba la "X" de allowClear).
  stationFilterOptions: { label: string; value: string }[] = [];
  workshifts = [];
  status = [];

  constructor(
    public gasprecioService: GasprecioService,
    private authenticationService: AuthService,
    private sharedService: SharedService,
    private formBuilder: UntypedFormBuilder,
    private fuelOrders: FuelOrdersService,
    private customAlert: CustomAlertService,
  ) {
    this.rol = localStorage.getItem("rol");
    this.idStation = localStorage.getItem("idStation");
    this.id = localStorage.getItem("id");
    this.idGroup =
      this.rol === "Dios"
        ? localStorage.getItem("group") || "60afa99655f4ee369d1df409"
        : localStorage.getItem("group");
    this.showEnerser =
      this.idGroup === "643f507358715227618de808" ||
      this.idGroup === "649216f6edc3bad5497001f9"
        ? true
        : false;
    this.showRendichicas =
      this.idGroup === "615efe30b318ce63b8971986" ? true : false;
    this.showRuta = this.idGroup === "668eaeceec578636ce2a789a" ? true : false;
    this.showCombuexpress =
      this.idGroup === "63fd4c8f1a5bfd390727f392" ? true : false;
    this.showMobico =
      this.idGroup === "6697df2bfb7789085d7d82b4" ? true : false;
    this.showMegagas =
      this.idGroup === "666cb6528c251f2c5d6204b5" ? true : false;
    this.showBurgos =
      this.idGroup === "652089ad94a59a35e91437da" ||
      this.idGroup === "65c2849672bf4238ffccbe4c"
        ? true
        : false;

    this.zone$ = sharedService.sharedZone.subscribe((zone: any) => {
      if (zone !== this.idZone) {
        if (this.first && this.showRendichicas) {
          this.idZone = "6178671b46818a57510e9218";
        } else if (
          this.first &&
          this.showCombuexpress &&
          this.id !== "63fe38d41a5bfd390736314c" &&
          this.id !== "63fe38d41a5bfd390736314e" &&
          this.id !== "63fe38d41a5bfd3907363150"
        ) {
          this.idZone = "63fea6de2df0863b8e7da412";
        } else if (
          this.first &&
          this.showEnerser &&
          this.idGroup === "643f507358715227618de808"
        ) {
          this.idZone = "651b4288b13c717f2fcd775b";
        } else if (
          this.first &&
          this.showEnerser &&
          this.idGroup === "649216f6edc3bad5497001f9"
        ) {
          this.idZone = "64d2d15f8bd81e4d428e7797";
        } else if (this.first && this.showMobico) {
          this.idZone = "6697e639fb7789085d7f7012";
        } else if (this.first && this.showMegagas) {
          this.idZone = "666cb9c38c251f2c5d622283";
        } else if (this.first && this.showRuta) {
          this.idZone = "668eb65057ab5f397ef9f2df";
        } else if (
          this.first &&
          this.showBurgos &&
          this.idGroup !== "65c2849672bf4238ffccbe4c"
        ) {
          this.idZone = "6525737f03da8cde44820771";
        } else {
          this.idZone = zone;
        }
        if (!this.loading && this.inited) {
          this.getData();
        }
      }
    });

    this.weekSelected$ = this.sharedService.sharedWeekSelected.subscribe(
      (weekSelected) => {
        // Guard por VALOR (fechas), no por referencia: this.weekSelected nunca se
        // asignaba, así que el `!==` de antes era SIEMPRE true y cualquier
        // re-emisión disparaba getData. app-select-week re-emite en su init un
        // interval NUEVO con las mismas fechas cuando nadie ha cambiado la semana
        // (getWeeksSchedule, this.week === ''), y con dos instancias en la vista
        // del solicitante (bandas Solicitudes y Cronograma) eso encadenaba
        // recargas duplicadas (bug 2026-07-09).
        const weekChanged =
          weekSelected["start_date"] !== this.startDate ||
          weekSelected["end_date"] !== this.endDate;
        if (weekChanged) {
          this.startDate = weekSelected["start_date"];
          this.endDate = weekSelected["end_date"];
          if (!this.loading && this.inited) {
            this.getData();
          }
        }
      },
    );

    this.idGroupSub$ = this.sharedService.sharedGroup.subscribe((idGroup) => {
      if (idGroup !== this.idGroup && idGroup !== "") {
        this.idGroup = idGroup;
        this.idZone = "";
        this.showEnerser =
          this.idGroup === "643f507358715227618de808" ||
          this.idGroup === "649216f6edc3bad5497001f9"
            ? true
            : false;
        this.showRendichicas =
          this.idGroup === "615efe30b318ce63b8971986" ? true : false;
        this.showRuta =
          this.idGroup === "668eaeceec578636ce2a789a" ? true : false;
        this.showCombuexpress =
          this.idGroup === "63fd4c8f1a5bfd390727f392" ? true : false;
        this.showMobico =
          this.idGroup === "6697df2bfb7789085d7d82b4" ? true : false;
        this.showMegagas =
          this.idGroup === "666cb6528c251f2c5d6204b5" ? true : false;
        this.showBurgos =
          this.idGroup === "652089ad94a59a35e91437da" ||
          this.idGroup === "65c2849672bf4238ffccbe4c"
            ? true
            : false;
        if (!this.loading && this.inited) {
          this.getData();
        }
      }
      this.getStationsGroup();
    });
  }

  ngOnInit(): void {
    this.idStation = localStorage.getItem("idStation")
      ? localStorage.getItem("idStation")
      : this.authenticationService.idStation;
    this.getListMarketers();
    this.getListWorkShifts();
    this.getListStatus();
    // Inputs ya asignados: ahora sí carga con el source/station correctos.
    this.inited = true;
    this.getData();
    this.newForm = this.formBuilder.group({
      stationSelected: ["", Validators.required],
      product: ["", Validators.required],
      marketSelected: ["", Validators.required],
      marketOther: [""],
      shiftSelected: ["", Validators.required],
      litersRequested: ["", Validators.required],
      statusSelected: ["", Validators.required],
      date: ["", Validators.required],
      expectedDeliverateDate: ["", Validators.required],
      deliverateDate: [""],
      cancellationDate: [""],
      total: ["", Validators.required],
      liters: [""],
    });
  }

  ngOnDestroy(): void {
    this.idGroupSub$.unsubscribe();
    this.weekSelected$.unsubscribe();
    this.zone$.unsubscribe();
  }

  /** Refresh interno (botón junto a la última actualización): recarga sin F5 ni loader. */
  refreshData(): void {
    if (this.loading) {
      return;
    }
    this.getData(true);
  }

  get n() {
    return this.newForm.controls;
  }

  getRowHeight(row: any) {
    return row.height;
  }

  // NOTA: la tabla Cronograma ya no usa altura fija — desde el rediseño se dimensiona
  // al contenido de la página (scrollbarV=false, ver template), como "Solicitudes", así
  // que no queda espacio muerto entre la última fila y el paginador. El getter de altura
  // fija se retiró; el empty-state conserva su alto vía .orders-cronograma .empty-row (SCSS).

  // La altura de la tabla Solicitudes vive en CSS (.orders-summary-scroll):
  // min-height de 3 filas, crece con el contenido y max-height de 10 filas
  // con scroll interno. Sin cálculos en px que se desfasen del render real.
  /**
   * El solicitante puede pedir cambios o cancelación solo mientras el pedido siga
   * SIN respuesta de MOBICO (pendiente / enviada / en revisión); una vez aprobado o
   * rechazado ya no hay acciones (feedback 2026-07-06). Además el pedido no debe
   * tener ya una solicitud de cambio en vuelo (un solo cambio a la vez; el backend
   * también lo valida). Aplica a los dos botones de Acciones de la tabla
   * Solicitudes (el cronograma es solo lectura).
   */
  canCancelOrder(row: any): boolean {
    if (!["pending", "sent", "review"].includes(row.status)) {
      return false;
    }
    return !this.pendingChangesByOrder.has(String(row._id));
  }

  /**
   * Motivo por el que las acciones del pedido están deshabilitadas (tooltip de
   * los botones "Cambiar"/"Cancelar"); null si el pedido sí admite acciones.
   * Task 2026-07-10 "los botones de acciones tienen que aparecer siempre":
   * en vez de ocultarlos (antes la celda mostraba '—'), quedan visibles con
   * enable/disable — mismo patrón que el trash de eliminar pipa del formulario.
   */
  orderActionsDisabledReason(row: any): string | null {
    if (this.canCancelOrder(row)) {
      return null;
    }
    if (["pending", "sent", "review"].includes(row.status)) {
      return "Este pedido ya tiene una solicitud en curso; espera la respuesta de MOBICO";
    }
    switch (row.status) {
      case "confirmed":
        return "El pedido ya fue aprobado por MOBICO";
      case "rejected":
        return "El pedido ya fue rechazado por MOBICO";
      case "cancelled":
        return "El pedido ya fue cancelado";
      default:
        return "Este pedido ya no admite cambios";
    }
  }

  // ---------- Modal "Realizar cambio" (el solicitante pide el cambio a MOBICO) ----------
  emptyChangeForm() {
    return {
      type: "date",
      newDate: "",
      newCapacity: null,
      newProduct: "",
      newMaterial: "",
    };
  }

  openChangeForm(row: any): void {
    this.changeRow = row;
    this.changeForm = this.emptyChangeForm();
    this.showChangeCalendar = false;
    this.refreshChangeSpace();
    this.showChangeForm = true;
  }

  cancelChangeForm(): void {
    this.showChangeForm = false;
    this.changeRow = null;
  }

  /**
   * Candado de inventario del cambio: espacio disponible con los valores efectivos
   * de la solicitud (producto/fecha nuevos si se están capturando, si no los del
   * pedido), excluyendo al propio pedido del cálculo (el cambio lo reemplaza).
   */
  refreshChangeSpace(): void {
    this.changeSpace = null;
    this.changeSpaceChecked = false;
    this.changeSpaceBasis = null;
    this.changeTankCapacity = null;
    this.changeInventoryUsed = null;
    this.changeInventoryDate = null;
    this.changeCarryOver = null;
    const row = this.changeRow;
    if (!row) {
      return;
    }
    const f = this.changeForm;
    const product =
      f.type === "product" ? f.newProduct || row.product : row.product;
    const date =
      f.type === "date"
        ? f.newDate
        : row.order_delivery_date
          ? moment(row.order_delivery_date).format("YYYY-MM-DD")
          : "";
    if (!product || !date) {
      return;
    }
    this.changeSpaceLoading = true;
    this.fuelOrders
      .getSuggest(row.station_id, product, date, row._id)
      .subscribe({
        next: (data: any) => {
          this.changeSpaceLoading = false;
          this.changeSpaceChecked = true;
          this.changeSpace = data?.available_space ?? null;
          this.changeSpaceBasis = data?.space_basis ?? null;
          this.changeTankCapacity = data?.tank_capacity ?? null;
          this.changeInventoryUsed = data?.inventory_used ?? null;
          this.changeInventoryDate = data?.inventory_date ?? null;
          this.changeCarryOver = data?.carry_over ?? null;
        },
        error: () => {
          this.changeSpaceLoading = false;
          this.changeSpaceChecked = true;
          this.changeSpace = null;
          this.changeSpaceBasis = null;
          this.changeTankCapacity = null;
          this.changeInventoryUsed = null;
          this.changeInventoryDate = null;
          this.changeCarryOver = null;
        },
      });
  }

  /** Litros que quedarían tras el cambio (capacidad nueva o los litros del pedido). */
  private get changeEffectiveLiters(): number | null {
    const f = this.changeForm;
    return f.type === "capacity"
      ? f.newCapacity
      : (this.changeRow?.liters_requested ?? null);
  }

  /** true si el cambio dejaría a la pipa por encima del espacio disponible. */
  get changeOverSpace(): boolean {
    const liters = this.changeEffectiveLiters;
    return (
      this.changeSpace != null && liters != null && liters > this.changeSpace
    );
  }

  // REGLA DESACTIVADA (2026-07-06) — escalera MOBICO de cambios anclada a las
  // 10:00 am (MX), espejo del backend: 'ok' hasta las 10:00 de dos días antes de
  // la entrega · 'waitlist' hasta las 10:00 del día previo (queda en espera de
  // disponibilidad) · 'late' después (el backend la rechazaba). Descomentar junto
  // con su uso en changeFormValid y los avisos del modal de cambio (HTML):
  // get changeDeadlineState(): 'ok' | 'waitlist' | 'late' | null {
  //   const row = this.changeRow;
  //   if (!row) { return null; }
  //   const f = this.changeForm;
  //   const date = f.type === 'date'
  //     ? f.newDate
  //     : (row.order_delivery_date ? moment(row.order_delivery_date).format('YYYY-MM-DD') : '');
  //   if (!date) { return null; }
  //   const deliveryStart = moment.tz(date, 'America/Mexico_City').startOf('day');
  //   const now = moment.tz('America/Mexico_City');
  //   if (now.isAfter(deliveryStart.clone().subtract(1, 'days').add(10, 'hours'))) { return 'late'; }
  //   if (now.isAfter(deliveryStart.clone().subtract(2, 'days').add(10, 'hours'))) { return 'waitlist'; }
  //   return 'ok';
  // }

  // ---- Mini calendario del cambio de fecha (ngb-datepicker inline) ----
  /** Mes inicial del calendario: el de la fecha requerida actual del pedido. */
  get calStartDate(): { year: number; month: number } | null {
    const d = this.changeRow?.order_delivery_date;
    if (!d) {
      return null;
    }
    const m = moment.utc(d);
    return { year: m.year(), month: m.month() + 1 };
  }
  /** Hoy como tope inferior: no tiene sentido mover una entrega al pasado. */
  get calMinDate(): { year: number; month: number; day: number } {
    const t = moment.tz("America/Mexico_City");
    return { year: t.year(), month: t.month() + 1, day: t.date() };
  }
  /** Hoy (MX) como tope inferior de "Fecha requerida" en Nueva solicitud (fix
   *  2026-07-10): no se puede pedir una pipa para una fecha ya pasada. Alimenta
   *  el [min] de los type=date (bloquea el calendario nativo) y rowDateTooEarly
   *  (bloquea lo tecleado a mano, que el min NO valida). Formato YYYY-MM-DD,
   *  el mismo del value de los type=date: comparables como string. */
  get minRequestDate(): string {
    return moment.tz("America/Mexico_City").format("YYYY-MM-DD");
  }
  /** true si la fila trae fecha requerida anterior a hoy (bloquea el envío vía
   *  orderRowValid y pinta el input en rojo vía is-invalid). */
  rowDateTooEarly(f: OrderFormRow): boolean {
    return !!f.loadDate && f.loadDate < this.minRequestDate;
  }
  private calKey(d: { year: number; month: number; day: number }): string {
    return `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
  }
  /** Día de la fecha requerida ACTUAL del pedido (marcado en amarillo). */
  isCalCurrent(d: any): boolean {
    const cur = this.changeRow?.order_delivery_date;
    return !!cur && moment.utc(cur).format("YYYY-MM-DD") === this.calKey(d);
  }
  /** Día elegido como NUEVA fecha (relleno verde). */
  isCalSelected(d: any): boolean {
    return (
      !!this.changeForm.newDate && this.changeForm.newDate === this.calKey(d)
    );
  }
  // Visible solo al dar click en "Nueva fecha"; se oculta al elegir el día.
  showChangeCalendar = false;
  // Posición del panel del calendario (task 2026-07-10 "que aparezca abajo de
  // Nueva fecha"): flota en capa fija ANCLADO al campo, como el popup del input
  // date nativo. Se calcula al abrir con el rect del input; alinea el borde
  // derecho del panel con el del campo (el campo vive al extremo derecho del
  // modal) y si no hay espacio abajo, se abre hacia arriba.
  calPanelPos: { top: string; right: string } | null = null;
  toggleChangeCalendar(ev: MouseEvent): void {
    this.showChangeCalendar = !this.showChangeCalendar;
    if (!this.showChangeCalendar) { return; }
    const r = (ev.target as HTMLElement).getBoundingClientRect();
    // Altura real del picker ≈ 320px (6 filas de 36 + weekdays + header + la
    // leyenda del footerTemplate + padding). Con una estimación holgada el flip
    // hacia arriba se disparaba antes de tiempo y quedaba despegado del campo.
    const panelH = 320;
    const gap = 6;
    let top = r.bottom + gap; // preferencia: SIEMPRE abajo del campo
    if (top + panelH > window.innerHeight - 12) {
      // Sin espacio abajo: pegado ARRIBA del campo (nunca flotando lejos).
      top = Math.max(12, r.top - gap - panelH);
    }
    // Respiro mínimo de 16px contra el borde derecho de la pantalla.
    const right = Math.max(16, window.innerWidth - r.right);
    this.calPanelPos = { top: `${top}px`, right: `${right}px` };
  }
  onCalSelect(d: any): void {
    this.changeForm.newDate = this.calKey(d);
    this.showChangeCalendar = false;
    this.refreshChangeSpace();
  }
  /** La fecha actual del pedido no se puede elegir: no habría cambio. */
  calMarkDisabled = (d: {
    year: number;
    month: number;
    day: number;
  }): boolean => this.isCalCurrent(d);

  /** El valor nuevo requerido depende del tipo de cambio. */
  get changeFormValid(): boolean {
    const f = this.changeForm;
    if (this.changeOverSpace) {
      return false;
    }
    // if (this.changeDeadlineState === 'late') { return false; } // REGLA DESACTIVADA (48/24 hrs)
    if (f.type === "date") {
      return !!f.newDate;
    }
    if (f.type === "capacity") {
      return !!f.newCapacity;
    }
    if (f.type === "product") {
      return !!f.newProduct;
    }
    return false;
  }

  /**
   * Envía la solicitud de cambio a MOBICO (POST /fuel-orders/:id/change). El pedido
   * original NO se modifica: el cambio se aplica hasta que MOBICO lo aprueba.
   */
  submitChangeForm(): void {
    if (!this.changeFormValid || this.savingChange) {
      return;
    }
    const f = this.changeForm;
    const payload: any = { change_type: f.type };
    if (f.type === "date") {
      payload.delivery_date = f.newDate;
    } else if (f.type === "capacity") {
      payload.truck_capacity = f.newCapacity;
      payload.liters_requested = f.newCapacity;
    } else if (f.type === "product") {
      payload.product = f.newProduct;
      payload.material = f.newMaterial || null;
    }

    this.savingChange = true;
    this.fuelOrders.requestChange(this.changeRow._id, payload).subscribe({
      next: () => {
        this.savingChange = false;
        this.showChangeForm = false;
        this.changeRow = null;
        this.getData();
        this.showToast(
          "success",
          "Solicitud de cambio enviada",
          "Aplicará cuando MOBICO la apruebe.",
        );
      },
      error: (err) => {
        console.error("Error solicitando cambio", err);
        this.savingChange = false;
        const msg = this.apiErrorMsg(
          err,
          "Revisa los datos o intenta de nuevo.",
        );
        this.customAlert.showError("No se pudo enviar la solicitud", msg);
      },
    });
  }

  // ---------- Modal de solicitud de pipa (fuel-orders) ----------
  emptyOrderForm(): OrderFormRow {
    return {
      station: "",
      loadDate: "",
      capacity: null,
      po: "",
      material: "",
      product: "",
    };
  }
  openOrderForm() {
    // REGLA DESACTIVADA (2026-07-06) — ventana de pedidos lunes 9-14; descomentar
    // junto con isOrderWindowOpen/orderWindowMsg para reactivar:
    // if (!this.isOrderWindowOpen) {
    //   this.customAlert.showInfo('Fuera del horario de pedidos', this.orderWindowMsg);
    //   return;
    // }
    this.orderEditMode = false;
    this.editingOrderId = "";
    this.orderForms = [this.emptyOrderForm()];
    this.showOrderForm = true;
  }

  /** Abre el modal en modo editar, precargando la pipa de la fila. */
  openEditOrder(row: any) {
    this.orderEditMode = true;
    this.editingOrderId = row._id;
    this.orderForms = [
      {
        station: row.station_id || "",
        loadDate: row.order_delivery_date
          ? moment(row.order_delivery_date).format("YYYY-MM-DD")
          : "",
        capacity: row.truck_capacity || row.liters_requested || null,
        po: row.po_number || "",
        material: row.material || "",
        product: row.product || "",
      },
    ];
    this.updateRowSpace(this.orderForms[0]);
    this.showOrderForm = true;
  }

  cancelOrderForm() {
    this.showOrderForm = false;
  }

  /** Agrega otra pipa a la solicitud. */
  addOrderRow() {
    this.orderForms.push(this.emptyOrderForm());
  }

  /** Quita una pipa de la solicitud (siempre queda al menos una). */
  removeOrderRow(i: number) {
    if (this.orderForms.length > 1) {
      this.orderForms.splice(i, 1);
    }
  }

  /**
   * Candado de inventario del modal: consulta el espacio disponible del tanque
   * proyectado a la fecha requerida (GET /suggest?date=). Espejo del candado del
   * backend: aquí bloquea el Enviar; allá, lo que se cuele cae a "review".
   * Se refresca al cambiar estación, tipo o fecha de la fila.
   */
  updateRowSpace(f: OrderFormRow): void {
    const station = this.onlyStation ? this.idStation : f.station;
    f.availableSpace = null;
    f.spaceChecked = false;
    f.tankCapacity = null;
    f.inventoryUsed = null;
    f.inventoryDate = null;
    f.carryOver = null;
    if (!station || !f.product || !f.loadDate) {
      return;
    }
    f.spaceLoading = true;
    const exclude = this.orderEditMode ? this.editingOrderId : "";
    this.fuelOrders
      .getSuggest(station, f.product, f.loadDate, exclude)
      .subscribe({
        next: (data: any) => {
          f.spaceLoading = false;
          f.spaceChecked = true;
          f.availableSpace = data?.available_space ?? null;
          f.spaceBasis = data?.space_basis ?? null;
          f.tankCapacity = data?.tank_capacity ?? null;
          f.inventoryUsed = data?.inventory_used ?? null;
          f.inventoryDate = data?.inventory_date ?? null;
          f.carryOver = data?.carry_over ?? null;
        },
        error: () => {
          f.spaceLoading = false;
          f.spaceChecked = true;
          f.availableSpace = null;
          f.spaceBasis = null;
          f.tankCapacity = null;
          f.inventoryUsed = null;
          f.inventoryDate = null;
          f.carryOver = null;
        },
      });
  }

  /**
   * Litros capturados en las OTRAS filas del formulario que restan espacio a
   * esta pipa: misma estación + producto con fecha anterior; a fecha igual
   * restan solo las filas de arriba (desempate por orden de captura para que
   * el hint "vaya bajando"). Espejo del batchLiters de saveOrders en el back —
   * si aquí cabe, allá no cae a revisión por espacio.
   */
  private rowBatchLiters(f: OrderFormRow): number {
    const idx = this.orderForms.indexOf(f);
    const station = this.onlyStation ? this.idStation : f.station;
    return this.orderForms.reduce((sum, b, i) => {
      if (b === f || !b.capacity || !b.loadDate || !f.loadDate) {
        return sum;
      }
      if (b.product !== f.product) {
        return sum;
      }
      const bStation = this.onlyStation ? this.idStation : b.station;
      if (bStation !== station) {
        return sum;
      }
      const before =
        b.loadDate < f.loadDate || (b.loadDate === f.loadDate && i < idx);
      return before ? sum + b.capacity : sum;
    }, 0);
  }

  /** Espacio disponible de la fila ya descontando lo capturado en filas previas. */
  rowEffectiveSpace(f: OrderFormRow): number | null {
    if (f.availableSpace == null) {
      return null;
    }
    return Math.max(0, f.availableSpace - this.rowBatchLiters(f));
  }

  /** true si la pipa pide más litros que el espacio disponible proyectado. */
  rowOverSpace(f: OrderFormRow): boolean {
    const space = this.rowEffectiveSpace(f);
    return space != null && f.capacity != null && f.capacity > space;
  }

  // ---- Modal "¿Por qué no hay espacio?" (task 2026-07-13 "modal de información
  // de por qué no me deja comprar"): desglose de la cuenta del candado —
  // capacidad del tanque − inventario a la fecha de entrega (real o TEÓRICO del
  // Cronograma, fix del back del mismo día) − arrastre − pipas de este
  // formulario que llegan antes = espacio disponible vs. lo capturado. Aplica a
  // la solicitud nueva y al modal de cambio (fecha/capacidad/producto). ----
  spaceInfo: {
    tank: number | null;
    inventory: number | null;
    basis: string | null;
    invDate: string | null;
    carry: number | null;
    batch: number;
    available: number | null;
    requested: number | null;
  } | null = null;

  openSpaceInfo(f: OrderFormRow): void {
    this.spaceInfo = {
      tank: f.tankCapacity ?? null,
      inventory: f.inventoryUsed ?? null,
      basis: f.spaceBasis ?? null,
      invDate: f.inventoryDate ?? null,
      carry: f.carryOver ?? null,
      batch: this.rowBatchLiters(f),
      available: this.rowEffectiveSpace(f),
      requested: f.capacity ?? null,
    };
  }

  openChangeSpaceInfo(): void {
    this.spaceInfo = {
      tank: this.changeTankCapacity,
      inventory: this.changeInventoryUsed,
      basis: this.changeSpaceBasis,
      invDate: this.changeInventoryDate,
      carry: this.changeCarryOver,
      batch: 0,
      available: this.changeSpace != null ? Math.max(0, this.changeSpace) : null,
      requested: this.changeEffectiveLiters,
    };
  }

  closeSpaceInfo(): void {
    this.spaceInfo = null;
  }

  /**
   * Separador de miles para los inputs de capacidad (es-MX, mismo formato que
   * ya usa la columna "Nuevo valor"): el input muestra "30,000" pero el modelo
   * se mantiene numérico para las validaciones de espacio y los payloads.
   */
  formatThousands(value: number | null): string {
    return value != null ? new Intl.NumberFormat("es-MX").format(value) : "";
  }

  parseThousands(value: string): number | null {
    const digits = (value || "").replace(/\D/g, "");
    return digits ? +digits : null;
  }

  /** Campos requeridos por fila. La estación solo se exige en modo grupo. */
  private orderRowValid(f: OrderFormRow): boolean {
    const stationOk = this.onlyStation ? true : !!f.station;
    return (
      !!(
        stationOk &&
        f.product &&
        f.po &&
        f.loadDate &&
        f.material &&
        f.capacity
      ) &&
      !this.rowOverSpace(f) &&
      !this.rowDateTooEarly(f)
    );
  }

  /** Todas las pipas capturadas deben estar completas para ENVIAR. */
  get orderFormValid(): boolean {
    return (
      this.orderForms.length > 0 &&
      this.orderForms.every((f) => this.orderRowValid(f))
    );
  }

  /** Payload de una pipa (fuel-orders) a partir de una fila del formulario. Lo usa
   *  submitOrderForm(), compartido por el modal multi-pipa y el formulario inline
   *  (ambos operan sobre orderForms). */
  private buildOrderPayload(f: OrderFormRow): any {
    return {
      product: f.product,
      delivery_date: f.loadDate,
      liters_requested: f.capacity,
      truck_capacity: f.capacity,
      po_number: f.po,
      material: f.material,
    };
  }

  /**
   * Envía la solicitud al backend (POST /fuel-orders, modo append) y refresca el cronograma.
   * El backend aplica un solo week_start por payload y una sola estación, así que agrupamos
   * las pipas por estación + semana ISO y enviamos un POST por grupo (normalmente uno solo).
   */
  submitOrderForm(): void {
    if (!this.orderFormValid || this.savingOrder) {
      return;
    }
    if (this.orderEditMode) {
      this.submitEditOrder();
      return;
    }

    const byKey = new Map<
      string,
      { station: string; week: string; orders: any[] }
    >();
    for (const f of this.orderForms) {
      const station = this.onlyStation ? this.idStation : f.station;
      const week = moment(f.loadDate).startOf("isoWeek").format("YYYY-MM-DD");
      const key = `${station}__${week}`;
      const order = this.buildOrderPayload(f);
      const bucket = byKey.get(key) || { station, week, orders: [] };
      bucket.orders.push(order);
      byKey.set(key, bucket);
    }

    const requests = Array.from(byKey.values()).map((b) =>
      this.fuelOrders.saveOrders({
        station: b.station,
        group: this.idGroup || null,
        week_start: b.week,
        orders: b.orders,
      }),
    );

    this.savingOrder = true;
    // Secuencial (concat), NO paralelo (forkJoin): el backend asigna order_number leyendo
    // el máximo actual, así que POSTs simultáneos colisionan con el índice único
    // (E11000 dup key). En serie cada uno lee el máximo ya actualizado.
    concat(...requests).subscribe({
      error: (err) => {
        console.error("Error guardando pedidos", err);
        this.savingOrder = false;
        const msg = this.apiErrorMsg(
          err,
          "Revisa los datos o intenta de nuevo.",
        );
        this.customAlert.showError("No se pudo enviar", msg);
      },
      complete: () => {
        this.savingOrder = false;
        this.showOrderForm = false;
        // Reinicia el formulario a una sola pipa vacía: tanto el modal (que ya cierra
        // con showOrderForm=false) como el inline multi-pipa (que sigue visible tras
        // enviar) parten de una pipa en blanco para la próxima solicitud.
        this.orderForms = [this.emptyOrderForm()];
        this.getData();
        this.showToast(
          "success",
          "Solicitud enviada",
          "MOBICO la revisará y responderá por este medio.",
        );
      },
    });
  }

  /** Guarda los cambios de una pipa existente (PUT /fuel-orders/:id). */
  private submitEditOrder(): void {
    const f = this.orderForms[0];
    const station = this.onlyStation ? this.idStation : f.station;
    const payload = {
      station,
      group: this.idGroup || null,
      product: f.product,
      delivery_date: f.loadDate,
      liters_requested: f.capacity,
      truck_capacity: f.capacity,
      po_number: f.po,
      material: f.material,
    };

    this.savingOrder = true;
    this.fuelOrders.updateFullOrder(this.editingOrderId, payload).subscribe({
      next: () => {
        this.showOrderForm = false;
        this.getData();
        this.showToast("success", "Solicitud actualizada");
      },
      error: (err) => {
        console.error("Error actualizando pedido", err);
        this.savingOrder = false;
        this.customAlert.showError(
          "No se pudo actualizar",
          "Revisa los datos o intenta de nuevo.",
        );
      },
      complete: () => {
        this.savingOrder = false;
      },
    });
  }

  /**
   * La cancelación también pasa por MOBICO: se registra como solicitud de cambio
   * (change_type: cancel) y el pedido sigue vigente hasta que el admin la aprueba
   * (al aprobarla, el backend deja el pedido como cancelado) o la rechaza.
   */
  warningDeleteOrder(row: any): void {
    this.cancelError = "";
    this.cancelTarget = row;
  }

  /** Cierra el modal de cancelación sin enviar nada. */
  closeCancelConfirm(): void {
    if (this.cancellingOrder) {
      return;
    }
    this.cancelTarget = null;
    this.cancelError = "";
  }

  /**
   * Envía la solicitud de cancelación a MOBICO. El botón muestra el avance (evita
   * dobles clicks); si el API falla, el error queda inline en el propio modal.
   */
  async confirmCancelOrder(): Promise<void> {
    const row = this.cancelTarget;
    if (!row || this.cancellingOrder) {
      return;
    }
    this.cancellingOrder = true;
    this.cancelError = "";
    try {
      await firstValueFrom(
        this.fuelOrders.requestChange(row._id, { change_type: "cancel" }),
      );
      this.cancelTarget = null;
      this.getData();
      this.showToast(
        "success",
        "Solicitud de cancelación enviada",
        "Aplicará cuando MOBICO la apruebe.",
      );
    } catch (err: any) {
      console.error("Error solicitando cancelación", err);
      this.cancelError = this.apiErrorMsg(
        err,
        "No se pudo enviar la solicitud, intenta de nuevo.",
      );
    } finally {
      this.cancellingOrder = false;
    }
  }

  /** Abre la edición directa de entrega (admin MOBICO) precargada con la fila. */
  openDeliveryEdit(row: any): void {
    this.deliveryEditRow = row;
    this.deliveryEditError = "";
    // Prefill en UTC (las fechas del back son medianoche UTC; moment() local corre un día en MX).
    this.deliveryEditDate = row.delivery_date
      ? moment.utc(row.delivery_date).format("YYYY-MM-DD")
      : "";
    this.deliveryEditTotal = row.total ?? null;
    this.deliveryEditPrice = row.supply_price ?? null;
  }

  cancelDeliveryEdit(): void {
    if (this.savingDeliveryEdit) {
      return;
    }
    this.deliveryEditRow = null;
    this.deliveryEditError = "";
  }

  get deliveryEditValid(): boolean {
    return !!this.deliveryEditDate;
  }

  /**
   * Guarda la edición directa: PATCH al pedido con fecha de entrega y costos. El
   * backend actualiza confirmed_delivery_date del pedido y la compra auto-generada,
   * así el cambio se refleja en todos los cronogramas y grids de inventario.
   */
  async submitDeliveryEdit(): Promise<void> {
    const row = this.deliveryEditRow;
    if (!row || !this.deliveryEditValid || this.savingDeliveryEdit) {
      return;
    }
    this.savingDeliveryEdit = true;
    this.deliveryEditError = "";
    const body: {
      delivery_date: string;
      total?: number;
      supply_price?: number;
    } = {
      delivery_date: this.deliveryEditDate,
    };
    if (this.deliveryEditTotal != null) {
      body.total = this.deliveryEditTotal;
    }
    if (this.deliveryEditPrice != null) {
      body.supply_price = this.deliveryEditPrice;
    }
    try {
      await firstValueFrom(this.fuelOrders.updateOrderStatus(row._id, body));
      this.deliveryEditRow = null;
      this.getData();
      this.showToast("success", "Entrega actualizada");
    } catch (err: any) {
      console.error("Error editando entrega", err);
      this.deliveryEditError = this.apiErrorMsg(
        err,
        "No se pudo guardar, intenta de nuevo.",
      );
    } finally {
      this.savingDeliveryEdit = false;
    }
  }

  changeClientG(cliente: any) {
    this.gasprecioService.changeClientG(cliente["cre_id"], cliente["_id"]);
  }

  filterTable($event?: any) {
    if ($event) {
      this.val = $event.target.value;
    }
    this.applyFilters();
  }

  /**
   * Filas base del Cronograma. En el flujo de solicitudes el cronograma es el
   * programa de entregas: SOLO pedidos confirmados por MOBICO, igual que el
   * cronograma de Inventario (que se alimenta de compras ya confirmadas). Las
   * solicitudes pendientes/rechazadas viven solo en la tabla Solicitudes.
   */
  private cronogramaBase(): any[] {
    const rows = this.rows || [];
    return this.source === "orders" && this.requestFlow
      ? rows.filter((r: any) => r.status === "confirmed")
      : rows;
  }

  /** Predicado compartido de los filtros activos (buscador, producto y estatus). */
  private matchesFilters(d: any): boolean {
    // Filtro por producto
    if (this.productFilter && d.product !== this.productFilter) {
      return false;
    }
    // Filtro por estatus ('sent' de legado cuenta como 'pending'/Pendiente).
    const status = d.status === "sent" ? "pending" : d.status;
    if (this.statusFilter && status !== this.statusFilter) {
      return false;
    }
    // Filtro por estación (barra moderna de pipes-request, Tablero). Se matchea por
    // station_id (no por alias) porque es la clave estable presente en las filas crudas.
    if (this.stationFilter && d.station_id !== this.stationFilter) {
      return false;
    }
    // Filtro por texto (si hay búsqueda)
    const search = (this.val || "").toLowerCase();
    if (!search) {
      return true;
    }
    return ["station_name", "product", "zone", "marketerName"].some(
      (key) =>
        d[key] != null && String(d[key]).toLowerCase().indexOf(search) !== -1,
    );
  }

  /** Filtra la tabla combinando el buscador (texto), el producto y el estatus. */
  applyFilters() {
    // Cambiar los filtros reconstruye el conjunto de ambas tablas: el render
    // incremental regresa al primer lote (el scroll vuelve arriba solo, porque
    // el contenido encoge).
    this.ordersRenderLimit = this.renderBatch;
    this.cronoRenderLimit = this.renderBatch;
    // En el flujo de solicitudes cada card filtra lo suyo: el Cronograma usa su
    // propio filtro (cronoProductFilter, banda de la card) y deja de heredar los
    // de Solicitudes (producto/estatus/estación). El Tablero legacy (!requestFlow)
    // conserva el predicado compartido matchesFilters tal cual.
    this.cronogramaFiltered = this.cronogramaBase().filter((d: any) =>
      this.requestFlow
        ? !this.cronoProductFilter || d.product === this.cronoProductFilter
        : this.matchesFilters(d),
    );
    this.applyCronoPage();
    this.refreshRequestRows();
  }

  /**
   * Recorta this.cronogramaFiltered al lote de render vigente (scroll infinito
   * client-side, sin paginación). this.temp (lo que consume la ngx-datatable)
   * es la porción ya pintada: crece por lotes vía onCronoScroll. El Cronograma
   * se comporta igual en las 3 vistas (guía, requestFlow y view=pipes).
   */
  applyCronoPage(): void {
    this.temp = this.cronogramaFiltered.slice(0, this.cronoRenderLimit);
    // Relleno con filas vacías hasta cronoPageSize: mismo criterio que Solicitudes
    // (ver refreshRequestRows) — la tabla conserva su altura fija aunque haya
    // pocas entregas. height:32 porque getRowHeight devuelve row.height. Solo si
    // hay ≥1 fila real (con 0 se deja vacío para que se muestre el emptyMessage).
    if (this.temp.length > 0) {
      const padded = [...this.temp];
      for (let i = padded.length; i < this.cronoPageSize; i++) {
        padded.push({ _id: `__empty_${i}`, _placeholder: true, height: 32 });
      }
      this.temp = padded;
    }
  }

  /** Scroll del contenedor del Cronograma: al acercarse al fondo (~4 filas) se
   *  pinta el siguiente lote (los datos ya están cargados, solo se difiere el
   *  render). */
  onCronoScroll(event: Event): void {
    if (this.cronoRenderLimit >= this.cronogramaFiltered.length) {
      return;
    }
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 128) {
      this.cronoRenderLimit += this.renderBatch;
      this.applyCronoPage();
    }
  }

  getStationsGroup() {
    this.gasprecioService
      .getStationsGroup(this.idGroup)
      .subscribe((data: any) => {
        const stations: any = data;
        stations.forEach((element: any) => {
          this.stations.push({ label: element.alias, value: element._id });
        });
        // Filtro de Estación (Ajuste A, allowClear=false): "Todas las estaciones" (value '')
        // primero, seguido de this.stations tal cual. Se rearma completo cada vez (no
        // this.stations.push) para no arrastrar duplicados si getStationsGroup se llama de nuevo.
        this.stationFilterOptions = [
          { label: "Todas las estaciones", value: "" },
          ...this.stations,
        ];
        this.showStations = true;
      });
  }

  getListMarketers() {
    this.gasprecioService.getListMarketers().subscribe((data: any) => {
      const marketers: any = data;
      marketers.forEach((element: any) => {
        this.marketers.push({ label: element.name, value: element._id });
      });
      this.marketers.push({ label: "OTRA", value: "1" });
      this.showMarketers = true;
    });
  }

  getListWorkShifts() {
    this.gasprecioService.getListWorkShifts().subscribe((data: any) => {
      const workshifts: any = data;
      workshifts.forEach((element: any) => {
        this.workshifts.push({ label: element.name, value: element._id });
      });
      this.showWorkShifts = true;
    });
  }

  getListStatus() {
    this.gasprecioService.getListStatus().subscribe((data: any) => {
      const status: any = data;
      status.forEach((element: any) => {
        this.status.push({ label: element.name, value: element._id });
      });
      this.showStatus = true;
    });
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      return false;
    }
    return true;
  }

  /**
   * Estatus visible de una solicitud de cambio con el tipo integrado, para que el
   * historial se lea directo (ej. "Cambio aprobado", "Cancelación rechazada").
   */
  private changeStatusDisplay(kind: string, c: any): string {
    // REGLA DESACTIVADA (2026-07-06) — estatus "En espera de disponibilidad" del
    // review por la regla de 48 hrs (nunca aplicaba a cancelaciones):
    // if (kind !== 'cancel' && c.status === 'review' && (c.week_status || '').includes('espera de disponibilidad')) {
    //   return 'En espera de disponibilidad';
    // }
    const subject =
      kind === "cancel"
        ? "Cancelación"
        : kind === "addition"
          ? "Adición"
          : "Cambio";
    const fem = kind === "cancel" || kind === "addition";
    const byStatus: { [key: string]: string } = {
      pending: `${subject} pendiente`,
      sent: `${subject} pendiente`,
      review: `${subject} en revisión`,
      confirmed: fem ? `${subject} aprobada` : `${subject} aprobado`,
      rejected: fem ? `${subject} rechazada` : `${subject} rechazado`,
    };
    return byStatus[c.status] || this.changeStatusLabels[c.status] || c.status;
  }

  /**
   * Carga las solicitudes de cambio de MOBICO sobre los pedidos de la semana. Se
   * aplana la respuesta agrupada por tipo a una sola lista con el tipo, el valor
   * nuevo ya formateado y la respuesta MOBICO; las filas se integran a la tabla
   * única "Solicitudes" como historial anidado bajo su pedido.
   */
  getChangesData() {
    const params = {
      group: this.idGroup,
      zones: this.idZone || "",
      week: this.startDate,
      station: this.onlyStation ? this.idStation : "",
    };
    this.fuelOrders.getChanges(params).subscribe({
      next: (data: any) => {
        const changes = data?.changes || {};
        const rows: any[] = [];
        // Se conserva TODO como historial: pendientes, aprobados y rechazados
        // (el estatus se distingue en la columna Respuesta MOBICO).
        Object.keys(this.changeTypeLabels).forEach((type) => {
          (changes[type] || []).forEach((c: any) => {
            let newValue = "—";
            if (type === "date" && c.new_delivery_date) {
              newValue = moment.utc(c.new_delivery_date).format("DD/MM/YY");
            } else if (type === "capacity" && c.new_truck_capacity) {
              newValue = new Intl.NumberFormat("es-MX").format(
                c.new_truck_capacity,
              );
            } else if (type === "product") {
              const productOption = this.orderProductOptions.find(
                (p) => p.value === c.new_product,
              );
              newValue =
                c.new_material ||
                (productOption ? productOption.label : c.new_product) ||
                "—";
            }
            rows.push({
              ...c,
              change_kind: type,
              type_label: this.changeTypeLabels[type],
              new_value: newValue,
              status_label: this.changeStatusDisplay(type, c),
              station_name: c.station,
              // Campos normalizados para compartir columnas con las filas de pedido.
              order_delivery_date: c.delivery_date,
              requested_at: c.created_at,
            });
          });
        });
        // Orden estándar del flujo: lo más reciente arriba (el backend ordena por tipo).
        rows.sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        this.changesRows = rows;
        const pendingByOrder = new Map<string, any[]>();
        rows
          .filter(
            (c: any) =>
              ["pending", "sent", "review"].includes(c.status) && c.order_id,
          )
          .forEach((c: any) => {
            const key = String(c.order_id);
            const list = pendingByOrder.get(key) || [];
            list.push(c);
            pendingByOrder.set(key, list);
          });
        this.pendingChangesByOrder = pendingByOrder;
        this.refreshRequestRows();
        this.changesLoadedForNotice = true;
        this.maybeShowResolvedNotices();
      },
      error: (err) => {
        console.error("Error cargando cambios", err);
        this.changesRows = [];
        this.pendingChangesByOrder = new Map<string, any[]>();
        this.refreshRequestRows();
        this.changesLoadedForNotice = true;
      },
    });
  }

  /**
   * Aviso único al solicitante cuando MOBICO resuelve (aprueba/rechaza) sus pedidos
   * o cambios: un toast resumen con los conteos (el detalle vive en la tabla), una
   * sola vez por resolución (los ids ya avisados se guardan en localStorage). La
   * primera vez solo se registra lo ya resuelto, sin aviso, para no avisar
   * historial viejo.
   */
  private maybeShowResolvedNotices(): void {
    // En la página "Cronograma" (cronoOnly) no se avisa NI se registra: el aviso
    // pertenece a la vista de solicitud, y marcar los ids como vistos acá haría
    // que allá ya no aparezca (de-dupe por localStorage).
    if (this.cronoOnly) {
      return;
    }
    if (!this.ordersLoadedForNotice || !this.changesLoadedForNotice) {
      return;
    }
    const resolvedOrders = (this.rows || []).filter((r: any) =>
      ["confirmed", "rejected"].includes(r.status),
    );
    const resolvedChanges = (this.changesRows || []).filter((c: any) =>
      ["confirmed", "rejected"].includes(c.status),
    );
    const allIds = [...resolvedOrders, ...resolvedChanges].map((x: any) =>
      String(x._id),
    );

    let seen: any = null;
    try {
      seen = JSON.parse(localStorage.getItem(this.noticeStorageKey));
    } catch {
      seen = null;
    }
    if (!Array.isArray(seen)) {
      localStorage.setItem(this.noticeStorageKey, JSON.stringify(allIds));
      return;
    }

    const seenSet = new Set(seen.map(String));
    const newOrders = resolvedOrders.filter(
      (r: any) => !seenSet.has(String(r._id)),
    );
    const newChanges = resolvedChanges.filter(
      (c: any) => !seenSet.has(String(c._id)),
    );
    if (!newOrders.length && !newChanges.length) {
      return;
    }
    // Si hay un Swal abierto (otro toast o un diálogo del legacy), reintenta en el
    // siguiente refresh: Swal es de instancia única y dispararía el cierre del actual.
    if (Swal.isVisible() || this.customAlert.isOpen) {
      return;
    }

    // Se marcan como avisados ANTES de mostrar: el aviso es una sola vez.
    const news = [...newOrders, ...newChanges];
    localStorage.setItem(
      this.noticeStorageKey,
      JSON.stringify([
        ...seen.map(String),
        ...news.map((x: any) => String(x._id)),
      ]),
    );

    // Aprobar un cambio CONFIRMA también el pedido padre (regla del back desde
    // 2026-07-08): esa confirmación es efecto del mismo "sí", no una respuesta
    // aparte. El padre queda registrado como visto (arriba), pero se excluye del
    // conteo para no avisar "2 aprobadas" por una sola solicitud.
    const confirmedByChange = new Set(
      newChanges
        .filter((c: any) => c.status === "confirmed" && c.order_id)
        .map((c: any) => String(c.order_id)),
    );
    const counted = [
      ...newOrders.filter(
        (r: any) =>
          !(r.status === "confirmed" && confirmedByChange.has(String(r._id))),
      ),
      ...newChanges,
    ];
    if (!counted.length) {
      return;
    }

    // Resumen por resultado: "2 aprobadas y 1 rechazada". El ícono acompaña al
    // resultado (verde todo aprobado, rojo todo rechazado, info mixto).
    const approved = counted.filter(
      (x: any) => x.status === "confirmed",
    ).length;
    const rejected = counted.length - approved;
    const parts: string[] = [];
    if (approved) {
      parts.push(approved === 1 ? "1 aprobada" : `${approved} aprobadas`);
    }
    if (rejected) {
      parts.push(rejected === 1 ? "1 rechazada" : `${rejected} rechazadas`);
    }
    const icon = rejected === 0 ? "success" : approved === 0 ? "error" : "info";
    this.showToast(
      icon,
      "MOBICO respondió tus solicitudes",
      `${parts.join(" y ")}. El detalle está en la tabla.`,
    );
  }

  /** Toast del flujo: aviso breve no bloqueante que se cierra solo. Con texto de
   *  detalle se fija un ancho cómodo para que la línea no se parta en pedazos
   *  (mismo trato que el toast del panel admin). Look moderno del flujo vía
   *  customClass .pipes-toast (styles.scss: card blanca radio 12 + ícono FA en
   *  círculo soft-tint, mismos tintes que los badges de la tabla); el iconHtml
   *  reemplaza el aro animado default de Swal. */
  private showToast(
    icon: "success" | "error" | "info",
    title: string,
    text?: string,
  ): void {
    const iconHtml = {
      success: '<i class="fa fa-check" aria-hidden="true"></i>',
      error: '<i class="fa fa-times" aria-hidden="true"></i>',
      info: '<i class="fa fa-info" aria-hidden="true"></i>',
    }[icon];
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      iconHtml,
      title,
      text,
      // 490 acompaña la escala grande del look .pipes-toast (feedback
      // 2026-07-10 "se ve muy pequeña"): con 430 la tipografía crecida
      // partía el detalle en 2 líneas.
      width: text ? 490 : undefined,
      showConfirmButton: false,
      showCloseButton: true,
      timer: 7000,
      timerProgressBar: true,
      customClass: { popup: `pipes-toast pipes-toast--${icon}` },
    });
  }

  /**
   * Arma la tabla única "Solicitudes": UNA fila por pedido, sin desglose. Si el
   * pedido tiene un cambio en vuelo, la fila muestra ese cambio (tipo, valor nuevo
   * y estatus); al resolverse, la fila vuelve a mostrar el pedido ya actualizado
   * por el backend. Los filtros de producto/estatus/búsqueda aplican sobre lo que
   * la fila muestra. Orden: el del backend (lo más reciente arriba).
   */
  refreshRequestRows(): void {
    if (!(this.source === "orders" && this.requestFlow)) {
      this.requestRows = [];
      return;
    }
    // Cambio EN VUELO por pedido (a lo más uno: el backend rechaza un segundo).
    const inFlightByOrder = new Map<string, any>();
    (this.changesRows || []).forEach((c: any) => {
      if (this.isChangeInFlight(c) && c.order_id) {
        inFlightByOrder.set(String(c.order_id), c);
      }
    });

    // Última actividad por pedido: el timestamp más reciente entre sus cambios
    // (updated_at = cuándo respondió MOBICO; created_at si sigue en vuelo). Es la
    // clave del ORDEN de la tabla: lo recién movido —solicitado, aprobado o
    // rechazado— sube. Antes se ordenaba por la fecha de creación de lo que la
    // fila muestra, y al resolverse un cambio la fila regresaba a su posición de
    // origen, "escondiendo" la respuesta recién llegada.
    const activityByOrder = new Map<string, number>();
    (this.changesRows || []).forEach((c: any) => {
      if (!c.order_id) {
        return;
      }
      const ts = new Date(c.updated_at || c.created_at).getTime() || 0;
      const key = String(c.order_id);
      activityByOrder.set(key, Math.max(activityByOrder.get(key) || 0, ts));
    });

    // Una fila por pedido, con su historial y su fecha de solicitud.
    const parents: any[] = [];
    (this.rows || []).forEach((r: any) => {
      const change = inFlightByOrder.get(String(r._id));
      const history = this.buildHistoryFor(r);
      const hasHistory = history.length > 0;
      // request_date = cuándo se creó lo que muestra la fila: el cambio en curso
      // si lo hay, si no el pedido.
      const parentRow = change
        ? {
            ...r,
            request_date: change.created_at,
            change_kind: change.change_kind,
            type_label: change.type_label,
            new_value: change.new_value,
            status: change.status,
            status_label: change.status_label,
            week_status: change.week_status,
            in_flight_change: true,
            has_history: hasHistory,
            _history: history,
          }
        : {
            ...r,
            request_date: r.date,
            change_kind: "addition",
            type_label: this.changeTypeLabels["addition"],
            new_value: "—",
            // Pedido confirmado VÍA un cambio aprobado (aprobar el cambio
            // confirma el pedido completo): el badge lo dice para que no se
            // lea igual que una adición confirmada tal cual se pidió.
            status_label:
              r.status === "confirmed" &&
              history.some((h: any) => h.status === "confirmed")
                ? "Cambio aprobado"
                : r.statusName,
            has_history: hasHistory,
            _history: history,
          };
      // Actividad del pedido: lo más reciente entre el propio pedido (updated_at
      // cubre creación, edición y respuesta de MOBICO) y sus cambios.
      parentRow._activity_ts = Math.max(
        new Date(r.updated_at || r.date).getTime() || 0,
        activityByOrder.get(String(r._id)) || 0,
      );
      if (!this.matchesFilters(parentRow)) {
        return;
      }
      parents.push(parentRow);
    });
    // Lo movido más recientemente arriba (creación o respuesta de MOBICO), igual
    // que la tabla del admin.
    parents.sort((a: any, b: any) => b._activity_ts - a._activity_ts);

    // Corte de render (scroll infinito client-side, sin paginación): se pintan
    // los primeros ordersRenderLimit padres del conjunto ya ordenado; el
    // historial se inserta después, solo para los padres visibles.
    this.ordersTotalItems = parents.length;
    const visibleParents = parents.slice(0, this.ordersRenderLimit);

    // Si un pedido está expandido, sus filas de historial se insertan justo debajo
    // ocupando las MISMAS columnas (se ven como parte de la tabla).
    const result: any[] = [];
    visibleParents.forEach((p: any) => {
      result.push(p);
      if (p.has_history && this.expandedHistory.has(String(p._id))) {
        p._history.forEach((ev: any) => result.push(ev));
      }
    });
    // Relleno con filas vacías hasta pipesPageSize: la tabla conserva su altura
    // fija aunque haya pocas solicitudes, sin hueco bajo la última. Solo si hay
    // ≥1 fila real (con 0 filas se deja vacío para que el emptyMessage se
    // muestre). El _id único mantiene el trackByProp/rowIdentity estables;
    // _placeholder marca la fila para ocultar su contenido vía CSS (ver
    // getRowClass + .pipes-empty-row).
    if (result.length > 0) {
      for (let i = result.length; i < this.pipesPageSize; i++) {
        result.push({ _id: `__empty_${i}`, _placeholder: true });
      }
    }
    this.requestRows = result;
  }

  /** Scroll del contenedor de Solicitudes: al acercarse al fondo (~4 filas) se
   *  pinta el siguiente lote de pedidos (los datos ya están cargados, solo se
   *  difiere el render). No mueve el historial expandido. */
  onOrdersScroll(event: Event): void {
    if (this.ordersRenderLimit >= this.ordersTotalItems) {
      return;
    }
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 128) {
      this.ordersRenderLimit += this.renderBatch;
      this.refreshRequestRows();
    }
  }

  /**
   * Historial de cambios de un pedido como filas hijas de la tabla Solicitudes: SOLO
   * los cambios ya RESUELTOS por MOBICO (aprobados/rechazados), más reciente arriba.
   * El cambio en vuelo no se lista: ya lo muestra la fila padre (listarlo también
   * lo duplicaba). El pedido de origen tampoco: ya es la fila padre. Cada evento
   * hereda el contexto del pedido (PO/Estación/Capacidad/Producto/Fecha requerida)
   * para renderizarse con TODAS las columnas de la tabla. Solo la semana en curso
   * (los cambios ya están en changesRows).
   */
  buildHistoryFor(order: any): any[] {
    // Contexto del pedido que comparten las filas del historial: así cada fila muestra
    // todos los datos de la tabla, no columnas en blanco.
    const base = {
      is_history: true,
      // Pedido dueño de la fila: lo usa el cierre animado para marcar SUS hijas.
      _order_id: String(order._id),
      po_number: order.po_number,
      station_name: order.station_name,
      truck_capacity: order.truck_capacity,
      material: order.material,
      order_delivery_date: order.order_delivery_date,
    };
    const events: any[] = [];
    (this.changesRows || [])
      .filter(
        (c: any) =>
          String(c.order_id) === String(order._id) && !this.isChangeInFlight(c),
      )
      .forEach((c: any) => {
        events.push({
          ...base,
          // Identidad estable de la fila (doc de cambio): la usa rowIdentity para
          // que ngx-datatable reutilice el DOM y no re-anime filas ya visibles.
          _id: c._id,
          change_kind: c.change_kind,
          type_label: c.type_label,
          request_date: c.created_at,
          new_value: c.new_value || "—",
          status: c.status,
          status_label: c.status_label,
          _ts: new Date(c.created_at).getTime(),
        });
      });
    // Más reciente arriba.
    events.sort((a: any, b: any) => b._ts - a._ts);
    return events;
  }

  /**
   * Alterna el desglose de historial de un pedido (inserta/quita sus filas hijas).
   * El cierre es en DOS tiempos para que haya animación de salida (ngx-datatable
   * no tiene exit hook): primero las filas hijas se marcan `_closing` (corre la
   * animación oh-conceal y el chevron regresa), y al terminar se retiran del array.
   */
  toggleHistory(row: any): void {
    const id = String(row._id);
    if (this.expandedHistory.has(id)) {
      if (this.closingHistory.has(id)) {
        return;
      }
      this.closingHistory.add(id);
      this.requestRows = this.requestRows.map((r: any) =>
        r.is_history && r._order_id === id ? { ...r, _closing: true } : r,
      );
      setTimeout(() => {
        this.closingHistory.delete(id);
        this.expandedHistory.delete(id);
        this.refreshRequestRows();
      }, 160);
      return;
    }
    this.expandedHistory.add(id);
    this.refreshRequestRows();
  }

  /** true si el desglose de historial del pedido está abierto (y no cerrándose). */
  isHistoryOpen(row: any): boolean {
    const id = String(row._id);
    return this.expandedHistory.has(id) && !this.closingHistory.has(id);
  }

  /** Marca las filas hijas del historial (y las que están saliendo) para el CSS. */
  getRowClass = (row: any) => ({
    "history-subrow": !!row?.is_history,
    "history-closing": !!row?._closing,
    "pipes-empty-row": !!row?._placeholder,
  });

  /** Cambios aún sin respuesta de MOBICO (definen qué muestra la fila del pedido). */
  private isChangeInFlight(c: any): boolean {
    return ["pending", "sent", "review"].includes(c.status);
  }

  /** @param silent true = recarga interna sin loader (refresh automático). */
  getData(silent = false) {
    if (!silent) {
      this.reqst = 0;
    }
    // Toda recarga arranca con los historiales COLAPSADOS (pedido del equipo:
    // al volver a entrar o refrescar, el historial oculto para no perderse);
    // la expansión es un gesto puntual de consulta, no un estado que persista.
    this.expandedHistory.clear();
    this.closingHistory.clear();
    this.loading = true;
    this.first = false;
    this.ordersLoadedForNotice = false;
    this.changesLoadedForNotice = false;
    this.idZone ??= "";
    if (this.startDate === undefined || this.endDate === undefined) {
      this.startDate = moment().startOf("isoWeek").toISOString();
      this.endDate = moment(this.startDate).add(6, "days").toISOString();
    }
    if (this.source === "orders" && this.requestFlow) {
      this.getChangesData();
    }
    this.gasprecioService
      .getListPurchases(
        this.idGroup,
        this.startDate,
        this.endDate,
        this.idZone,
        this.source,
        this.onlyStation ? this.idStation : "",
      )
      .subscribe({
        next: (data: any) => {
          this.rows = data;
          this.rows.forEach((element: any) => {
            this.status.forEach((status: any) => {
              if (status.value === element.status) {
                element.statusName = status.label;
                if (element.statusName === "Entregada") {
                  element["status_date"] = element["delivery_date"];
                } else if (element.statusName === "Cancelada") {
                  element["status_date"] = element["cancellation_date"];
                } else {
                  element["status_date"] = element["expected_delivery_date"];
                }
              }
            });
            this.marketers.forEach((marketer: any) => {
              if (marketer.value === element.marketer) {
                element.marketerName = marketer.label;
              }
            });
            this.workshifts.forEach((shift: any) => {
              if (shift.value === element.shift) {
                element.shiftName = shift.label;
              }
            });
            element.height = 32;
            element.zone =
              element.zone != null
                ? this.gasprecioService.capitalizeFirstLetter(element.zone)
                : "";
            if (element.zone === "Valle De Chalco Solidaridad") {
              element.zone = "Valle De Chalco";
            }
            element.zone =
              element.zone != null
                ? this.gasprecioService.capitalizeFirstLetter(element.zone)
                : "";
            if (element.zone === "Ciudad De México") {
              element.zone = "CDMX";
            }
            element.icon = true;
          });

          this.cronogramaFiltered = this.cronogramaBase().map(
            (prop: any, key: any) => {
              return {
                ...prop,
                id: key,
              };
            },
          );

          // Reaplica los filtros activos (producto/estatus/búsqueda) tras recargar;
          // applyFilters ya refresca también la tabla Solicitudes y repagina el
          // Cronograma. Sin filtros activos se repagina el Cronograma aquí mismo
          // (this.temp siempre debe ser la porción de UNA página, nunca el conjunto
          // completo que acaba de recargarse).
          if (
            this.productFilter ||
            this.statusFilter ||
            this.stationFilter ||
            this.cronoProductFilter ||
            this.val
          ) {
            this.applyFilters();
          } else {
            this.cronoRenderLimit = this.renderBatch;
            this.applyCronoPage();
            this.refreshRequestRows();
          }
          if (this.source === "orders" && this.requestFlow) {
            this.ordersLoadedForNotice = true;
            this.maybeShowResolvedNotices();
          }
        },
        complete: () => {
          this.reqst = 1;
          this.loading = false;
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  changeMarketer(e) {
    const marketerSelected = e.target.value;
    this.marketerName = marketerSelected.label;
    this.newForm.get("marketSelected").setValue(marketerSelected.value, {
      onlySelf: true,
    });
  }

  changeProduct(e) {
    const productSelected = e.target.value;
    this.newForm.get("product").setValue(productSelected.value, {
      onlySelf: true,
    });
  }

  changeStation(e) {
    const stationSelected = e.target.value;
    this.newForm.get("stationSelected").setValue(stationSelected.value, {
      onlySelf: true,
    });
  }

  changeShift(e) {
    const shiftSelected = e.target.value;
    this.newForm.get("shiftSelected").setValue(shiftSelected.value, {
      onlySelf: true,
    });
  }

  changeStatus(e) {
    const statusSelected = e.target.value;
    this.statusName = statusSelected.label;
    this.newForm.get("statusSelected").setValue(statusSelected.value, {
      onlySelf: true,
    });
  }

  changeMode(mode) {
    this.mode = mode;
  }

  addPipe() {
    if (this.newForm.invalid) {
      this.gasprecioService.displayMessage(
        "warning",
        "Hemos detectado que falta uno o más datos, favor de ingresarlos",
      );
      return;
    }

    if (this.marketerName === "OTRA") {
      this.addMarket();
    } else {
      let json: any;
      const date = moment(this.n["date"].value).tz("America/Mexico_City");
      const expectedDeliveryDate = moment(
        this.n["expectedDeliverateDate"].value,
      ).tz("America/Mexico_City");
      if (this.statusName === "Entregada") {
        const supplyPrice = this.n["total"].value / this.n["liters"].value;
        const statusDate = moment(this.n["deliverateDate"].value).tz(
          "America/Mexico_City",
        );
        json = {
          data: {
            purchases: [
              {
                station: this.n["stationSelected"].value,
                product: this.n["product"].value,
                liters_requested: this.n["litersRequested"].value,
                liters: this.n["liters"].value,
                supply_price: supplyPrice,
                total: this.n["total"].value,
                marketer: this.n["marketSelected"].value,
                status: this.n["statusSelected"].value,
                date: date,
                delivery_date: statusDate,
                expected_delivery_date: expectedDeliveryDate,
                shift: this.n["shiftSelected"].value,
              },
            ],
          },
        };
      } else if (this.statusName === "Cancelada") {
        const statusDate = moment(this.n["cancellationDate"].value).tz(
          "America/Mexico_City",
        );

        json = {
          data: {
            purchases: [
              {
                station: this.n["stationSelected"].value,
                product: this.n["product"].value,
                liters_requested: this.n["litersRequested"].value,
                total: this.n["total"].value,
                marketer: this.n["marketSelected"].value,
                status: this.n["statusSelected"].value,
                date: date,
                cancellation_date: statusDate,
                expected_delivery_date: expectedDeliveryDate,
                shift: this.n["shiftSelected"].value,
              },
            ],
          },
        };
      } else {
        json = {
          data: {
            purchases: [
              {
                station: this.n["stationSelected"].value,
                product: this.n["product"].value,
                liters_requested: this.n["litersRequested"].value,
                total: this.n["total"].value,
                marketer: this.n["marketSelected"].value,
                status: this.n["statusSelected"].value,
                date: date,
                expected_delivery_date: expectedDeliveryDate,
                shift: this.n["shiftSelected"].value,
              },
            ],
          },
        };
      }

      if (this.mode === "add") {
        this.gasprecioService.addPurchase(json).subscribe({
          next: () => {
            this.successPipe();
          },
          error: () => {
            this.gasprecioService.displayMessage(
              "error",
              "¡Error!",
              "La pipa no se ha registrado con éxito",
            );
          },
        });
      } else if (this.mode === "edit") {
        json["data"]["purchases"][0]["_id"] = this.idPurchase;
        this.gasprecioService.editPurchase(json).subscribe({
          next: () => {
            this.successEditPipe();
          },
          error: () => {
            this.gasprecioService.displayMessage(
              "error",
              "¡Error!",
              "La pipa no se ha actualizado con éxito",
            );
          },
        });
      }
    }
  }

  addMarket() {
    const name = this.n["marketOther"].value;
    const info = {
      data: {
        marketers: [
          {
            name: name.toUpperCase(),
          },
        ],
      },
    };

    this.gasprecioService.addMarketer(info).subscribe({
      next: (inf) => {
        const marketer = inf["data"]["marketers"][0];
        this.marketerName = marketer.name;
        this.newForm.get("marketSelected").setValue(marketer._id, {
          onlySelf: true,
        });
        this.marketers.push({ label: marketer.name, value: marketer._id });

        let json: any;
        const date = moment(this.n["date"].value).tz("America/Mexico_City");
        const expectedDeliveryDate = moment(
          this.n["expectedDeliveryDate"].value,
        ).tz("America/Mexico_City");
        if (this.statusName === "Entregada") {
          const supplyPrice = this.n["total"].value / this.n["liters"].value;
          const statusDate = moment(this.n["deliverateDate"].value).tz(
            "America/Mexico_City",
          );
          json = {
            data: {
              purchases: [
                {
                  station: this.n["stationSelected"].value,
                  product: this.n["product"].value,
                  liters_requested: this.n["litersRequested"].value,
                  liters: this.n["liters"].value,
                  supply_price: supplyPrice,
                  total: this.n["total"].value,
                  marketer: this.n["marketSelected"].value,
                  status: this.n["statusSelected"].value,
                  date: date,
                  delivery_date: statusDate,
                  expected_delivery_date: expectedDeliveryDate,
                  shift: this.n["shiftSelected"].value,
                },
              ],
            },
          };
        } else if (this.statusName === "Cancelada") {
          const statusDate = moment(this.n["cancellationDate"].value).tz(
            "America/Mexico_City",
          );
          json = {
            data: {
              purchases: [
                {
                  station: this.n["stationSelected"].value,
                  product: this.n["product"].value,
                  liters_requested: this.n["litersRequested"].value,
                  total: this.n["total"].value,
                  marketer: this.n["marketSelected"].value,
                  status: this.n["statusSelected"].value,
                  date: date,
                  cancellation_date: statusDate,
                  expected_delivery_date: expectedDeliveryDate,
                  shift: this.n["shiftSelected"].value,
                },
              ],
            },
          };
        } else {
          json = {
            data: {
              purchases: [
                {
                  station: this.n["stationSelected"].value,
                  product: this.n["product"].value,
                  liters_requested: this.n["litersRequested"].value,
                  total: this.n["total"].value,
                  marketer: this.n["marketSelected"].value,
                  status: this.n["statusSelected"].value,
                  date: date,
                  expected_delivery_date: expectedDeliveryDate,
                  shift: this.n["shiftSelected"].value,
                },
              ],
            },
          };
        }

        this.gasprecioService.addPurchase(json).subscribe({
          next: () => {
            this.successPipe();
          },
          error: () => {
            this.gasprecioService.displayMessage(
              "error",
              "¡Error!",
              "La pipa no se ha registrado con éxito",
            );
          },
        });
      },
      error: () => {
        this.gasprecioService.displayMessage(
          "error",
          "¡Error!",
          "La pipa no se ha registrado con éxito",
        );
      },
    });
  }

  async successPipe() {
    Swal.fire({
      title: "¡Pipa registrada!",
      text: "La pipa se ha registrado con éxito",
      icon: "success",
      confirmButtonColor: "#00e07b",
      confirmButtonText: "Aceptar",
      allowEscapeKey: false,
      allowEnterKey: false,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.value) {
        this.getData();
      }
    });
  }

  async successEditPipe() {
    Swal.fire({
      title: "¡Pipa actualizada!",
      text: "La pipa se ha actualizado con éxito",
      icon: "success",
      confirmButtonColor: "#00e07b",
      confirmButtonText: "Aceptar",
      allowEscapeKey: false,
      allowEnterKey: false,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.value) {
        this.getData();
      }
    });
  }

  resetValues() {
    this.newForm.reset();
    this.product = "Seleccionar Producto";
    this.marketSelected = "Seleccionar Comercializadora";
    this.stationSelected = "Seleccionar Estación";
    this.shiftSelected = "Seleccionar Turno";
    this.statusSelected = "Seleccionar Estatus";
    this.statusName = "";
    this.marketerName = "";
  }

  warningDeletePipe(idPipe) {
    Swal.fire({
      title: "¿Estás seguro?",
      text: `Al aceptar, borrarás la pipa de la lista`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00e07b",
      cancelButtonColor: "#fa1975",
      confirmButtonText: "Sí, borrarla",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        this.deletePipe(idPipe);
      }
    });
  }

  deletePipe(idPipe: any) {
    this.gasprecioService.deletePurchasePanel(idPipe).subscribe({
      next: () => {
        this.successDeletePipe();
      },
      error: () => {
        this.gasprecioService.displayMessage(
          "error",
          "¡Error!",
          "La pipa no se ha eliminado con éxito",
        );
      },
    });
  }

  async successDeletePipe() {
    Swal.fire({
      title: "¡Pipa eliminada!",
      text: "La pipa se ha eliminado con éxito",
      icon: "success",
      confirmButtonColor: "#00e07b",
      confirmButtonText: "Aceptar",
      allowEscapeKey: false,
      allowEnterKey: false,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.value) {
        this.getData();
      }
    });
  }

  editPipe(pipe: any) {
    let pipeValues;
    this.idPurchase = pipe._id;
    this.product = pipe.product;
    this.marketSelected = pipe.marketer;
    this.stationSelected = pipe.station_name;
    this.shiftSelected = pipe.shiftName;
    this.statusSelected = pipe.statusName;
    this.statusName = pipe.statusName;
    this.marketerName = pipe.marketerName;

    this.stations.forEach((element: any) => {
      if (element.label === pipe.station_name) {
        pipe.idStation = element.value;
      }
    });

    const dateR = moment(pipe.date)
      .tz("America/Mexico_City")
      .format("YYYY-MM-DD");

    if (pipe.statusName === "Entregada") {
      pipeValues = {
        marketSelected: pipe.marketer,
        product: pipe.product,
        stationSelected: pipe.idStation,
        shiftSelected: pipe.shift,
        statusSelected: pipe.status,
        litersRequested: pipe.liters_requested,
        liters: pipe.liters,
        total: pipe.total,
        date: dateR,
        expectedDeliverateDate: moment(pipe.expected_delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
        deliverateDate: moment(pipe.delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
      };
    } else if (pipe.statusName === "Cancelada") {
      pipeValues = {
        marketSelected: pipe.marketer,
        product: pipe.product,
        stationSelected: pipe.idStation,
        shiftSelected: pipe.shift,
        statusSelected: pipe.status,
        litersRequested: pipe.liters_requested,
        total: pipe.total,
        date: dateR,
        expectedDeliverateDate: moment(pipe.expected_delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
        cancellationDate: moment(pipe.cancellation_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
      };
    } else {
      pipeValues = {
        marketSelected: pipe.marketer,
        product: pipe.product,
        stationSelected: pipe.idStation,
        shiftSelected: pipe.shift,
        statusSelected: pipe.status,
        litersRequested: pipe.liters_requested,
        total: pipe.total,
        date: dateR,
        expectedDeliverateDate: moment(pipe.expected_delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
      };
    }
    this.newForm.patchValue(pipeValues);
  }
}
